"use client";

import { formatElapsed } from "@workspace/voice-shared/live-call";
import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import type { MagnusLiveCallState } from "./controller";
import type { LiveCallState } from "./store";

/** What the state hooks read: a controller, or anything shaped like one. */
export type LiveCallSubscribable = {
  getState: () => MagnusLiveCallState;
  subscribe: (listener: () => void) => () => void;
};

export type LiveCallCalmCall = Omit<
  LiveCallState,
  "inputLevel" | "outputLevel"
>;

/** The controller's state without the meter levels. */
export type LiveCallCalmState = Omit<MagnusLiveCallState, "call"> & {
  call: LiveCallCalmCall;
};

export type LiveCallMeterLevels = { inputLevel: number; outputLevel: number };

function sameApartFrom<T extends object>(
  a: T,
  b: T,
  skip: readonly (keyof T)[]
): boolean {
  for (const key of Object.keys(b) as (keyof T)[]) {
    if (!skip.includes(key) && a[key] !== b[key]) {
      return false;
    }
  }
  return true;
}

/** Maps each state to its calm view, reusing the last view when only the
 *  levels moved. */
export function calmLiveCallSelector(): (
  state: MagnusLiveCallState
) => LiveCallCalmState {
  let last: { source: MagnusLiveCallState; calm: LiveCallCalmState } | null =
    null;
  return (source) => {
    if (last?.source === source) {
      return last.calm;
    }
    let calm: LiveCallCalmState;
    if (
      last &&
      sameApartFrom(last.source, source, ["call"]) &&
      sameApartFrom(last.source.call, source.call, [
        "inputLevel",
        "outputLevel",
      ])
    ) {
      calm = last.calm;
    } else {
      const { inputLevel: _input, outputLevel: _output, ...call } = source.call;
      calm = { ...source, call };
    }
    last = { source, calm };
    return calm;
  };
}

/** Maps each state to its levels, reusing the last pair while they hold. */
export function liveCallLevelsSelector(): (
  state: MagnusLiveCallState
) => LiveCallMeterLevels {
  let last: LiveCallMeterLevels = { inputLevel: 0, outputLevel: 0 };
  return ({ call: { inputLevel, outputLevel } }) => {
    if (last.inputLevel !== inputLevel || last.outputLevel !== outputLevel) {
      last = { inputLevel, outputLevel };
    }
    return last;
  };
}

function useSelected<T>(
  controller: LiveCallSubscribable,
  createSelector: () => (state: MagnusLiveCallState) => T
): T {
  const [select] = useState(createSelector);
  const getSnapshot = useCallback(
    () => select(controller.getState()),
    [controller, select]
  );
  return useSyncExternalStore(controller.subscribe, getSnapshot, getSnapshot);
}

/**
 * The controller's state, holding its identity across the meter's level
 * ticks (every ~60 ms in a call) so context values and effects built on it
 * only change when something else does. Read levels with
 * `useLiveCallLevels`, in the one component that draws them.
 */
export function useLiveCallState(
  controller: LiveCallSubscribable
): LiveCallCalmState {
  return useSelected(controller, calmLiveCallSelector);
}

/** Just the meter levels, for the component that draws them. */
export function useLiveCallLevels(
  controller: LiveCallSubscribable
): LiveCallMeterLevels {
  return useSelected(controller, liveCallLevelsSelector);
}

/** The call's running time as `m:ss`, ticking each second while
 *  `startedAt` is set. */
export function useLiveCallElapsed(startedAt: string | null): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!startedAt) {
      return;
    }
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [startedAt]);
  return startedAt ? formatElapsed(now - Date.parse(startedAt)) : "0:00";
}
